import { useUserStore } from '@/stores/user'
import { useContainerStore } from '@/stores/containers'
import { ActionType, type Action } from '@/actions/Action'
import type { ContainerMove } from '@/actions/ContainerMove'
import type { ContainerWeighing } from '@/actions/ContainerWeighing'
import type { NewContainer } from '@/actions/NewContainer'
import type { NewIncident } from '@/actions/NewIncident'
import { ContainerStatus } from '@/models/Container'

interface RequestParameters {
  apiUrl: string
  contentType?: string
  body: string | FormData
}

export const syncAction = async (action: Action) => {
  if (!navigator.onLine) {
    console.warn(`No sync because device is offline`)
    updateStores(action, false)
    return false
  }

  const userStore = useUserStore()
  if (!userStore.isAuthenticated) {
    userStore.logout()
    throw new Error(`No sync because user is not authenticated`)
  }

  let requestParams = getRequestParameters(action)
  if (!requestParams) {
    throw new Error(`Failed to create a request for this action type: ${action.type}`)
  }

  let headers: any = {}
  headers['Authorization'] = 'Bearer ' + userStore.authenticatedUser?.token
  if (requestParams.contentType) {
    headers['Content-Type'] = requestParams.contentType
  }

  const response = await fetch(requestParams.apiUrl, {
    method: 'POST',
    headers: headers,
    body: requestParams.body,
  })
  const data = response.status !== 204 ? await response.json() : null

  if (response.status === 400) {
    throw new Error(`Failed to sync, bad request: ${JSON.stringify(data)}`)
  }

  if (response.status === 401) {
    userStore.logout()
    throw new Error(`Failed to sync, unauthorized: ${JSON.stringify(data)}`)
  }

  const success = response.ok
  if (!success) {
    console.warn(`Failed to sync, no bad request, will retry: ${JSON.stringify(data)}`)
  }

  updateStores(action, success, data)

  return success
}

const getRequestParameters = (action: Action): RequestParameters | null => {
  const type = action.type

  if (type === ActionType.ContainerMove) {
    return getContainerMoveRequestParameters(action as ContainerMove)
  } else if (type === ActionType.ContainerWeighing) {
    return getContainerWeighingRequestParameters(action as ContainerWeighing)
  } else if (type === ActionType.NewContainer) {
    return getNewContainerRequestParameters(action as NewContainer)
  } else if (type === ActionType.NewIncident) {
    return getNewIncidentRequestParameters(action as NewIncident)
  }

  return null
}

const getContainerMoveRequestParameters = (containerMove: ContainerMove): RequestParameters => {
  const containerStore = useContainerStore()
  const container = containerStore.findByCode(containerMove.containerCode)

  const apiUrl =
    import.meta.env.VITE_USE_MOCKS !== 'true'
      ? `${import.meta.env.VITE_API_URL}/containers/${container?.uuid}/move`
      : new URL(`/src/mocks/post/container_move.json`, import.meta.url).href
  const body = JSON.stringify(containerMove.payload)
  const contentType = 'application/json'

  return {
    apiUrl,
    contentType,
    body,
  }
}

const getContainerWeighingRequestParameters = (
  containerWeighing: ContainerWeighing,
): RequestParameters => {
  const containerStore = useContainerStore()
  const container = containerStore.findByCode(containerWeighing.containerCode)

  const apiUrl =
    import.meta.env.VITE_USE_MOCKS !== 'true'
      ? `${import.meta.env.VITE_API_URL}/containers/${container?.uuid}/weigh`
      : new URL(`/src/mocks/post/container_weighing.json`, import.meta.url).href
  const contentType = 'application/json'
  const body = JSON.stringify(containerWeighing.payload)

  return {
    apiUrl,
    contentType,
    body,
  }
}

const getNewContainerRequestParameters = (newContainer: NewContainer): RequestParameters => {
  const apiUrl =
    import.meta.env.VITE_USE_MOCKS !== 'true'
      ? `${import.meta.env.VITE_API_URL}/containers`
      : new URL(`/src/mocks/post/new_container.json`, import.meta.url).href
  const contentType = 'application/json'
  const body = JSON.stringify(newContainer.payload)

  return {
    apiUrl,
    contentType,
    body,
  }
}

const getNewIncidentRequestParameters = (newIncident: NewIncident): RequestParameters => {
  const apiUrl =
    import.meta.env.VITE_USE_MOCKS !== 'true'
      ? `${import.meta.env.VITE_API_URL}/incidents`
      : new URL(`/src/mocks/post/new_incident.json`, import.meta.url).href

  const body = new FormData()
  body.append('data', JSON.stringify(newIncident.payload.data))

  if (newIncident.payload.picture) {
    const base64 = newIncident.payload.picture

    const [metadata, base64Data] = base64.split(',')
    const byteString = atob(base64Data)
    const mimeType = metadata.match(/:(.*?);/)?.[1] || 'application/octet-stream'

    const byteNumbers = new Uint8Array(byteString.length)
    for (let i = 0; i < byteString.length; i++) {
      byteNumbers[i] = byteString.charCodeAt(i)
    }

    const file = new File([byteNumbers], 'picture', { type: mimeType })
    body.append('picture', file)
  }

  return {
    apiUrl,
    body,
  }
}

const updateStores = (action: Action, success: boolean, data?: any) => {
  if (action.type === ActionType.NewContainer) {
    updateContainerStore(action as NewContainer, success, data)
  }
}

const updateContainerStore = (action: NewContainer, success: boolean, data?: any) => {
  const containerStore = useContainerStore()
  let container = containerStore.findByCode(action.payload.code)

  if (!container) {
    container = {
      code: action.payload.code,
      typeUuid: action.payload.typeUuid,
      roomUuid: action.payload.roomUuid,
      status: ContainerStatus.Available,
    }
    containerStore.containers.push(container)
  }

  if (success) {
    container.uuid = data.uuid
  }
}
